import type { AnalisePorCriterio, SolicitacaoAnalise } from "../../services";
import { STATUS_ANALISE_CRITERIO } from "../../services";
import type { SecaoDocumento } from "./models";
import {
  analiseCriterioComRespostaAposSolicitacao,
  listarAnalisesCriterio,
  pickAnaliseLinhaEdicaoParecer,
} from "./analiseCriterioList";

export { analiseCriterioComRespostaAposSolicitacao } from "./analiseCriterioList";

export function secaoChaveParaCriterioNaAnalise(secao: SecaoDocumento): string {
  const criterioId = secao.criterioSolicitacaoId;
  return typeof criterioId === "number" && Number.isFinite(criterioId)
    ? String(criterioId)
    : secao.id;
}


function chavesCriterioAnaliseApi(c: {
  id: number;
  criterioId?: number;
}): string[] {
  const keys = new Set<string>();
  keys.add(String(c.id));
  const alt = c.criterioId;
  if (typeof alt === "number" && Number.isFinite(alt) && alt !== c.id) {
    keys.add(String(alt));
  }
  return Array.from(keys);
}

type CriterioComStatus = {
  id: number;
  criterioId?: number;
  status?: { id: number; nome?: string } | null;
  statusId?: number;
};

function statusIdAnalisePreferLinha(
  linha: AnalisePorCriterio | null,
  c: CriterioComStatus,
): number | null {
  const aId = linha?.status?.id;
  if (typeof aId === "number" && Number.isFinite(aId)) return aId;
  const cSt = c.status?.id;
  if (typeof cSt === "number" && Number.isFinite(cSt)) return cSt;
  if (typeof c.statusId === "number" && Number.isFinite(c.statusId)) {
    return c.statusId;
  }
  return null;
}


function criterioParecerFechadoParaIndicadorAdequacao(
  linha: AnalisePorCriterio | null,
  c: CriterioComStatus,
  parecerAnalistaPorCriterioKey?: Record<string, string | undefined> | null,
): boolean {
  if (parecerAnalistaPorCriterioKey) {
    for (const k of chavesCriterioAnaliseApi(c)) {
      if (
        !Object.prototype.hasOwnProperty.call(parecerAnalistaPorCriterioKey, k)
      ) {
        continue;
      }
      const u = parecerAnalistaPorCriterioKey[k];
      if (u === "DEVOLVER") return false;
      if (u === "CONFORME" || u === "NAO_CONFORME") return true;
      if (u === "") {
        const temSolicitacaoNoCicloAtual =
          Boolean(linha?.solicitadoEm) &&
          String(linha.solicitadoEm).trim() !== "";
        return !temSolicitacaoNoCicloAtual;
      }
    }
  }

  const sid = statusIdAnalisePreferLinha(linha, c);
  if (typeof sid === "number" && Number.isFinite(sid)) {
    if (sid === STATUS_ANALISE_CRITERIO.Devolver) return false;
    return (
      sid === STATUS_ANALISE_CRITERIO.Pendente ||
      sid === STATUS_ANALISE_CRITERIO.Conforme ||
      sid === STATUS_ANALISE_CRITERIO.NaoConforme
    );
  }

  return false;
}


export function buildDestaqueAlteracaoAnalistaPorSecaoId(
  analise: SolicitacaoAnalise | null,
  parecerAnalistaPorCriterioKey?: Record<string, string | undefined> | null,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!analise?.grupos?.length) return out;

  for (const g of analise.grupos) {
    for (const c of g.criterios || []) {
      const linha = pickAnaliseLinhaEdicaoParecer(c.analise);
      const destacar =
        Boolean(linha && analiseCriterioComRespostaAposSolicitacao(linha)) &&
        !criterioParecerFechadoParaIndicadorAdequacao(
          linha,
          c,
          parecerAnalistaPorCriterioKey,
        );

      if (destacar) {
        for (const k of chavesCriterioAnaliseApi(c)) {
          out[k] = true;
        }
      }
    }
  }

  return out;
}


export function buildDestaqueAdequacaoSindicatoPorSecaoId(
  todasSecoes: SecaoDocumento[],
  liberadosIds: number[] | null | undefined,
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!Array.isArray(liberadosIds) || liberadosIds.length === 0) return out;

  const set = new Set(liberadosIds);
  for (const s of todasSecoes) {
    const cid = s.criterioSolicitacaoId;
    if (cid != null && set.has(cid)) {
      out[s.id] = true;
    }
  }

  return out;
}


export function mapAdequacaoAnalisePorSecaoId(
  analise: SolicitacaoAnalise | null,
  todasSecoes: SecaoDocumento[],
  parecerAnalistaPorCriterioKey?: Record<string, string | undefined> | null,
): {
  
  solicitadaAoSindicatoPorSecaoId: Record<string, boolean>;
  
  aguardandoRetornoSindicatoPorSecaoId: Record<string, boolean>;
  
  respondidaPeloSindicatoPorSecaoId: Record<string, boolean>;
} {
  const solicitadaPorCriterio = new Map<string, boolean>();
  const aguardandoPorCriterio = new Map<string, boolean>();
  const respondidaPorCriterio = new Map<string, boolean>();

  if (analise?.grupos?.length) {
    for (const g of analise.grupos) {
      for (const c of g.criterios || []) {
        const linha = pickAnaliseLinhaEdicaoParecer(c.analise);
        const ciclos = listarAnalisesCriterio(c.analise);
        const chavesCriterio = chavesCriterioAnaliseApi(c);
        const fechado = criterioParecerFechadoParaIndicadorAdequacao(
          linha,
          c,
          parecerAnalistaPorCriterioKey,
        );

        const temSolAtual =
          Boolean(linha?.solicitadoEm) &&
          String(linha.solicitadoEm).trim() !== "";

        // Para o índice, considerar somente o ciclo atual. Histórico antigo com
        // `solicitadoEm` não deve reativar badge "adequação enviada".
        const temSolicitacao = !fechado && temSolAtual;

        const temCicloAguardando = ciclos.some(
          (x) =>
            Boolean(x?.solicitadoEm) &&
            String(x.solicitadoEm).trim() !== "" &&
            !analiseCriterioComRespostaAposSolicitacao(x),
        );

        const temCicloRespondido = ciclos.some(
          (x) =>
            Boolean(x?.solicitadoEm) &&
            String(x.solicitadoEm).trim() !== "" &&
            analiseCriterioComRespostaAposSolicitacao(x),
        );

        const parecerLocalPendente = Boolean(
          parecerAnalistaPorCriterioKey &&
          chavesCriterio.some(
            (k) =>
              Object.prototype.hasOwnProperty.call(
                parecerAnalistaPorCriterioKey,
                k,
              ) && parecerAnalistaPorCriterioKey[k] === "",
          ),
        );

        const fechadoEfetivo =
          fechado && !(parecerLocalPendente && temCicloRespondido);

        const aguardando = !fechadoEfetivo && temCicloAguardando;

        const respondida =
          !fechadoEfetivo && !temCicloAguardando && temCicloRespondido;

        for (const ck of chavesCriterio) {
          solicitadaPorCriterio.set(
            ck,
            temSolicitacao || (!fechadoEfetivo && temCicloRespondido),
          );
          aguardandoPorCriterio.set(ck, aguardando);
          respondidaPorCriterio.set(ck, respondida);
        }
      }
    }
  }

  const solicitadaAoSindicatoPorSecaoId: Record<string, boolean> = {};
  const aguardandoRetornoSindicatoPorSecaoId: Record<string, boolean> = {};
  const respondidaPeloSindicatoPorSecaoId: Record<string, boolean> = {};

  for (const s of todasSecoes) {
    const ck = secaoChaveParaCriterioNaAnalise(s);
    solicitadaAoSindicatoPorSecaoId[s.id] =
      solicitadaPorCriterio.get(ck) ?? false;
    aguardandoRetornoSindicatoPorSecaoId[s.id] =
      aguardandoPorCriterio.get(ck) ?? false;
    respondidaPeloSindicatoPorSecaoId[s.id] =
      respondidaPorCriterio.get(ck) ?? false;
  }

  return {
    solicitadaAoSindicatoPorSecaoId,
    aguardandoRetornoSindicatoPorSecaoId,
    respondidaPeloSindicatoPorSecaoId,
  };
}
